import { CSSVariablesResolver } from '@mantine/core';

const px = (value: number) => `${value}px`;

/**
 * Projects `theme.other.surface` into the CSS custom properties the stylesheets read.
 *
 * This is deliberately only a projection — every value, and the reasoning behind it, lives on the
 * theme in `weinvest.theme.ts`, so that file stays the one place to read the project's scale. The
 * indirection exists because `createTheme` has no slot for arbitrary CSS variables, and stylesheets
 * (`Section`, `CustomTable`, `BelgiumCoverageMap`, `PageGround`) are what consume these.
 *
 * A resolver rather than a `:root` block in `global.css` so the light/dark split is Mantine's to
 * apply: it emits each set under the colour-scheme selector Mantine itself switches on.
 */
export const surfaceVariablesResolver: CSSVariablesResolver = ({ other: { surface } }) => ({
	variables: {
		'--surface-section-radius': px(surface.radius.section),
		'--surface-object-radius': px(surface.radius.object),
		'--surface-pad-inline': px(surface.padding.inline),
		'--surface-pad-block': px(surface.padding.block),
		'--surface-head-pad-block': px(surface.padding.header),
		'--surface-foot-pad-block': px(surface.padding.footer),
		/* An alias for Mantine's own `md`, not a new value: the gap between cards is ordinary spacing. */
		'--surface-gap': 'var(--mantine-spacing-md)',
	},
	light: {
		'--surface-page-bg': surface.light.pageBg,
		'--surface-detail-bg': surface.light.detailBg,
		'--surface-section-bg': surface.light.sectionBg,
		'--surface-section-border-color': surface.light.sectionBorder,
		'--surface-object-bg': surface.light.objectBg,
		'--surface-rule-color': surface.light.rule,
		'--surface-header-fill': surface.light.headerFill,
		'--surface-header-color': surface.light.headerColor,
	},
	dark: {
		'--surface-page-bg': surface.dark.pageBg,
		'--surface-detail-bg': surface.dark.detailBg,
		'--surface-section-bg': surface.dark.sectionBg,
		'--surface-section-border-color': surface.dark.sectionBorder,
		'--surface-object-bg': surface.dark.objectBg,
		'--surface-rule-color': surface.dark.rule,
		'--surface-header-fill': surface.dark.headerFill,
		'--surface-header-color': surface.dark.headerColor,
	},
});
